import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

import { IconsModule } from '../../icons/icons.module';

export type AlertTone =
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'neutral'
  | 'plain';

/**
 * `featured` is the billing comparator's banner: strong tints and a ringed
 * icon. `subtle` is the cancellation notice: 50-level tints, a bare icon,
 * tighter radius — the same card, quieter.
 */
export type AlertAppearance = 'featured' | 'subtle';

/**
 * Card variant rendered as a coloured status alert: a tone-driven featured icon,
 * a projected content column and an optional trailing actions slot ([alertActions]).
 * Tones map to the design system's success / error / warning / brand palettes,
 * plus a neutral grey and a plain white for states that carry no colour.
 */
@Component({
  standalone: true,
  selector: 'app-alert-card',
  imports: [IconsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./alert-card.component.css'],
  template: `
    <div
      class="alert alert--{{ tone() }} alert--{{ appearance() }}"
      [class.alert--start]="align() === 'start'"
    >
      <span class="featured-icon featured-icon--{{ tone() }}">
        @if (tone() === 'success') {
          <app-check-circle-icon
            [width]="iconSize()"
            [height]="iconSize()"
            [color]="iconColor()"
          />
        } @else {
          <app-alert-circle-icon
            [width]="iconSize()"
            [height]="iconSize()"
            [color]="iconColor()"
          />
        }
      </span>

      <div class="alert-content">
        <ng-content />
      </div>

      <ng-content select="[alertActions]" />
    </div>
  `,
})
export class AlertCardComponent {
  readonly tone = input.required<AlertTone>();

  readonly appearance = input<AlertAppearance>('featured');

  readonly align = input<'center' | 'start'>('center');

  // The palette has no 600 step for success; the others are its 600s.
  private readonly iconColors: Record<AlertTone, string> = {
    success: '#16a34a',
    error: 'var(--error-600)',
    warning: 'var(--warning-600)',
    info: 'var(--brand-600)',
    neutral: 'var(--gray-500)',
    plain: 'var(--gray-500)',
  };

  readonly iconColor = computed(() => this.iconColors[this.tone()]);

  /** The subtle card draws its icon bare, at the design's 20px; the ringed one at 16px. */
  readonly iconSize = computed(() =>
    this.appearance() === 'subtle' ? '20px' : '16px'
  );
}
